import { ApiService, type ApiRequestConfig } from "@/services/api.service";
import type { TRequestStatusReason, TRequestStatusReasonGroup } from "@/types/intake";

export interface TRequestReasonCreate {
  label: string;
}

export interface TRequestReasonUpdate {
  label?: string;
  is_active?: boolean;
}

/**
 * The workspace's disposition-reason lists, as the Settings editor manages them
 * (#4286). The reviewer's picker reads the same endpoint uncached through
 * `useRequestReasons`, so an admin's edits show up in an open decision dialog
 * without invalidation.
 */
export class RequestReasonService extends ApiService {
  /** `includeInactive` is what the editor needs and the picker must not have:
   *  a retired reason stays resolvable for history but is never offered for a
   *  new disposition. */
  async list(
    slug: string,
    group: TRequestStatusReasonGroup,
    { includeInactive = false } = {},
    config?: ApiRequestConfig,
  ): Promise<TRequestStatusReason[]> {
    const params = new URLSearchParams({ group });
    if (includeInactive) params.set("include_inactive", "1");
    return this.get<TRequestStatusReason[]>(
      `/api/workspaces/${slug}/request-reasons/?${params.toString()}`,
      config,
    );
  }

  async create(
    slug: string,
    group: TRequestStatusReasonGroup,
    data: TRequestReasonCreate,
  ): Promise<TRequestStatusReason> {
    return this.post<TRequestStatusReason>(`/api/workspaces/${slug}/request-reasons/`, {
      ...data,
      group,
    });
  }

  async update(
    slug: string,
    _group: TRequestStatusReasonGroup,
    reasonId: string,
    data: TRequestReasonUpdate,
  ): Promise<TRequestStatusReason> {
    return this.patch<TRequestStatusReason>(
      `/api/workspaces/${slug}/request-reasons/${reasonId}/`,
      data,
    );
  }

  async remove(slug: string, _group: TRequestStatusReasonGroup, reasonId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/request-reasons/${reasonId}/`);
  }

  /** One drag: put `move` just before or after `target`, against the server's
   *  order as it is when the move lands, so two admins dragging at once both
   *  keep their move (NG-2798). Answers the group in its new order. */
  async move(
    slug: string,
    group: TRequestStatusReasonGroup,
    { move, target, after }: { move: string; target: string; after: boolean },
  ): Promise<TRequestStatusReason[]> {
    return this.patch<TRequestStatusReason[]>(`/api/workspaces/${slug}/request-reasons/`, {
      group,
      move,
      [after ? "after" : "before"]: target,
    });
  }
}
